import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from '@stencil/core';

import { getCSSVariable } from '../../../../services/utils';

/**
 * @part divider - The `pk-divider` used inside the shadow DOM of the accordion item
 */
@Component({
  tag: 'pk-accordion-item',
  styleUrl: './pk-accordion-item.scss',
  shadow: true,
})
export class PkAccordion {
  // Own Properties
  // ====================
  private cssVarPrefix = '--pk-accordion';

  private titleElement: HTMLPkAccordionItemTitleElement;
  private contentElement: HTMLPkAccordionItemContentElement;

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkAccordionItemElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() private _expanded = false;
  @State() private itemIndex: number;

  // Public Property API
  // ========================

  /** Item is disabled */
  @Prop({ reflect: true }) disabled: boolean = false;

  /** Default expand state */
  @Prop({ reflect: true }) expanded: boolean = false;

  /** Specify a background color token to be used when the accordion item is expanded. */
  @Prop({ reflect: true }) expandedBgColor: string;

  /** Specify if the item has divider */
  @Prop({ reflect: true }) hasDivider: boolean = true;

  // Prop lifecycle events
  // =======================

  @Watch('expanded')
  setExpanded(expanded: boolean) {
    expanded ? this.expand() : this.collapse();
  }

  @Watch('disabled')
  setDisabled(value: boolean) {
    this.titleElement.disabled = value;
    this.contentElement.disabled = value;
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** On toggle expand/collpase state */
  @Event() pkToggle: EventEmitter<{ expanded: boolean }>;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  connectedCallback() {
    this.titleElement = this.el.querySelector('pk-accordion-item-title');
    this.contentElement = this.el.querySelector('pk-accordion-item-content');

    if (!this.titleElement && !this.contentElement) {
      console.error('Please add title and content for accordion!');
      return;
    }

    this.setChildElementAttributes();
    if (this.expanded) {
      this.contentElement.expanded = this.expanded;
      this.titleElement.expanded = this.expanded;
      this._expanded = this.expanded;
    }

    this.setIndex();
  }

  // Listeners
  // ==============

  @Listen('pkTriggerToggle')
  onAccordionItemClick() {
    this.toggle();
  }

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  /** Close collapsible item */
  @Method()
  collapse() {
    this._expanded = false;

    this.setExpandState(false);

    return Promise.resolve({ expanded: false });
  }

  /** Open collapsible item */
  @Method()
  expand() {
    if (this.disabled) return;

    this.setExpandState(true);
    return Promise.resolve({ expanded: true });
  }

  /** Toggle collapsible item */
  @Method()
  toggle() {
    if (this.disabled) return;

    const newState = !this._expanded;

    this.setExpandState(newState);
    this.pkToggle.emit({ expanded: newState });

    return Promise.resolve({ expanded: newState });
  }

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private setExpandState = (newState: boolean) => {
    this._expanded = newState;

    this.titleElement.expanded = newState;
    this.contentElement.expanded = newState;
  };

  private setChildElementAttributes = () => {
    this.titleElement.disabled = this.disabled;
    this.contentElement.disabled = this.disabled;
  };

  private setIndex = () => {
    const allSimilarElements = document.querySelectorAll('pk-accordion-item');

    this.itemIndex = allSimilarElements.length ? Array.from(allSimilarElements).indexOf(this.el) : 0;

    this.titleElement.setIndex(this.itemIndex);
    this.contentElement.setIndex(this.itemIndex);
  };

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    const styles = {
      ...(this._expanded &&
        this.expandedBgColor && {
          [`${this.cssVarPrefix}--bg-color`]: getCSSVariable(this.expandedBgColor),
        }),
    };

    return (
      <Host
        style={{ ...styles }}
        id={`accordion-item-${this.itemIndex}`}
        class={{
          'accordion-item': true,
          'is-expanded': this._expanded,
          'is-disabled': this.disabled,
        }}
      >
        <slot />
        <pk-divider
          class={{
            'transition-all': true,
            'mt-m block md:mt-l': this._expanded,
            '!hidden': !this.hasDivider,
          }}
          direction="horizontal"
          part="divider"
        />
      </Host>
    );
  }
}
